import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DumbTask } from '../DumbTask';
import { Colors } from '../getTaskColors';
import { DuoTaskHowItWorksDialog } from './DuoTaskHowItWorksDialog';
import { ReferralLink } from './ReferralLink';
import type { ThresholdsDuoTaskCampaign_taskCampaign } from './__generated__/index.graphql';

const Footer = styled(Vertical)`
  padding: var(--intermediate-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);

  @media ${laptopAndAbove} {
    max-width: 50%;
  }
`;

type Props = {
  campain: ThresholdsDuoTaskCampaign_taskCampaign;
};
export const ThresholdsDuoTaskCampaign = ({ campain }: Props) => {
  const { shareUrl, rewardConfigs, description, shareMessage } = campain;

  return (
    <DumbTask
      key={campain.slug}
      color={Colors.BLUE}
      borderColor="rgb(var(--c-rgb-superRare))"
      title={
        <Horizontal>
          <LabelL bold>{campain.name}</LabelL>
          <LabelM color="var(--c-yellow-400)" bold>
            <Horizontal gap={0.5} as="span">
              <FontAwesomeIcon icon={faClock} />
              <TimeLeft time={campain.endDate} />
            </Horizontal>
          </LabelM>
        </Horizontal>
      }
      description={<BodyS>{description}</BodyS>}
      detailsDialog={({ open, onClose }) => (
        <DuoTaskHowItWorksDialog
          open={!!open}
          onClose={onClose}
          campain={campain}
        />
      )}
      footer={
        <Footer>
          <SBHorizontal>
            <LabelM bold>
              <FormattedMessage {...glossary.rewards} />
            </LabelM>
            <Horizontal>
              {rewardConfigs.map(rewardConfig => (
                <AnyRewardConfig
                  key={rewardConfig.id}
                  anyRewardConfig={rewardConfig}
                  variant={Variant.INLINE}
                />
              ))}
            </Horizontal>
          </SBHorizontal>
          <ReferralLink
            shareUrl={shareUrl || ''}
            shareMessage={shareMessage || undefined}
            buttonsSize="compact"
          />
        </Footer>
      }
    />
  );
};

ThresholdsDuoTaskCampaign.fragments = {
  taskCampaign: gql`
    fragment ThresholdsDuoTaskCampaign_taskCampaign on TaskCampaign {
      slug
      name
      endDate
      description
      shareUrl
      shareMessage
      rewardConfigs(rarity: $rarity) {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ...DuoTaskHowItWorksDialog_taskCampaign
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${DuoTaskHowItWorksDialog.fragments.taskCampaign}
  ` as TypedDocumentNode<ThresholdsDuoTaskCampaign_taskCampaign>,
};
